import clsx from 'clsx'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import moment from 'moment'
import type { RefObject } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import FlexibleDateSearchField from './FlexibleDateSearchField'
import SearchDateDropDown from './SearchDateDropDown'

interface Props {
  dropdownAnchorRef: RefObject<HTMLElement | null>
  onDateToggle: (e: React.MouseEvent<HTMLButtonElement>) => void
  onDatesApply: () => void
  onDatesDecline: () => void
  isToggled: boolean
  anytimeDateSelected?: boolean
  selectAnytimeDate?: (shouldApply: boolean) => void
  selectSpecificDates?: () => void
  selectFlexibleDates?: () => void
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  durationOptionsCustom?: Array<DurationOption>
  isSpecificSearchEnabled?: boolean
  className?: string
  showFlexibleYearSelector?: boolean
  closeMenu?: () => void
  showAIGlow?: boolean
  /** Called when user manually selects dates in the calendar */
  onUserDatesChanged?: (dates: App.TypeaheadExtractedTravelDates) => void
}

function SearchDateInputDesktop(props: Props) {
  const {
    dropdownAnchorRef,
    onDateToggle,
    isToggled,
    onDatesApply,
    onDatesDecline,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    durationOptionsCustom,
    isSpecificSearchEnabled = true,
    className,
    showFlexibleYearSelector,
    closeMenu,
    showAIGlow = false,
    onUserDatesChanged,
  } = props
  const { checkinDate, checkoutDate, flexibleMonths, flexibleNights, userSelectedFlexibleMonths } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const intl = useIntl()

  /**
   * When user only select checkin date, and close the dropdown, dismiss that value
   */
  const toggleDates = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!isToggled) {
      onDateToggle(e)
    }
  }, [isToggled, onDateToggle])

  useEffect(() => {
    if (!isToggled) {
      if (!checkinDate) {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
      }
      if (flexibleMonths && flexibleNights) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.FLEXIBLE })
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isToggled])

  const checkInFieldRef = useRef<HTMLButtonElement>(null)
  const checkOutFieldRef = useRef<HTMLButtonElement>(null)
  const checkInAndOutFieldsRefs = useMemo(() => [checkInFieldRef, checkOutFieldRef], [])

  return (
    <>
      {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME && <SearchFormField
        label={intl.formatMessage({
          id: 'IRYpNHhIsJ-g0WsJ7hUR',
          defaultMessage: 'When?',
          description: 'Form field label for the date field when anytime is selected',
        })}
        placeholder={intl.formatMessage({
          id: 'RigImioe5lWt2RsFEZL9',
          defaultMessage: 'Anytime',
          description: 'Placeholder for date field indicating any date is acceptable',
        })}
        onClick={onDateToggle}
      />}
      {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC && <SearchFormFieldGroup>
        <SearchFormField
          ref={checkInFieldRef}
          className={clsx({ 'focus-within': isToggled && !checkoutDate && !checkinDate })}
          label={intl.formatMessage({
            id: 'BZImHfWDmEabHe95A4Ie',
            defaultMessage: 'Check in',
            description: 'Form field label for the hotel check-in date in the booking date picker',
          })}
          onClick={toggleDates}
          placeholder={intl.formatMessage({
            id: 'RigImioe5lWt2RsFEZL9',
            defaultMessage: 'Anytime',
            description: 'Placeholder for date field indicating any date is acceptable',
          })}
          value={checkinDate ? moment(checkinDate).format(DMY_CASUAL_FORMAT) : undefined}
          showAIGlow={showAIGlow}
        />
        <SearchFormField
          ref={checkOutFieldRef}
          className={clsx({ 'focus-within': isToggled && checkinDate && !checkoutDate })}
          label={intl.formatMessage({
            id: 'zNj1m3NcaWN8Inz8iuy9',
            defaultMessage: 'Check out',
            description: 'Label for check-out date field in the date selection or calendar picker',
          })}
          onClick={toggleDates}
          value={checkoutDate ? moment(checkoutDate).format(DMY_CASUAL_FORMAT) : undefined}
          showAIGlow={showAIGlow}
          placeholder={intl.formatMessage({
            id: 'RigImioe5lWt2RsFEZL9',
            defaultMessage: 'Anytime',
            description: 'Placeholder for date field indicating any date is acceptable',
          })}
        />
      </SearchFormFieldGroup>}
      {dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE && <FlexibleDateSearchField
        className={className}
        onDateToggle={onDateToggle}
        flexibleMonths={userSelectedFlexibleMonths ? flexibleMonths : undefined}
        flexibleNightsId={flexibleNights}
        durationOptionsCustom={durationOptionsCustom}
        showAIGlow={showAIGlow}
      />}
      <SearchDateDropDown
        anchorRef={dropdownAnchorRef}
        triggerRef={checkInAndOutFieldsRefs}
        open={isToggled}
        onDatesApply={onDatesApply}
        onDatesDecline={onDatesDecline}
        anytimeDateSelected={anytimeDateSelected}
        selectAnytimeDate={selectAnytimeDate}
        selectSpecificDates={selectSpecificDates}
        selectFlexibleDates={selectFlexibleDates}
        dateSearchOptionId={dateSearchOptionId}
        durationOptionsCustom={durationOptionsCustom}
        isSpecificSearchEnabled={isSpecificSearchEnabled}
        showFlexibleYearSelector={showFlexibleYearSelector}
        closeMenu={closeMenu}
        onUserDatesChanged={onUserDatesChanged}
      />
    </>
  )
}

export default SearchDateInputDesktop
